import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { FC, useState, useRef, useEffect } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";

const mapStateToProps = (state: ApplicationState) => {
    return {
        productsDataView: getProductListDataView(state),
    };
};

interface OwnProps extends WidgetProps {
    fields: {};
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProduct & HasProductContext;

export interface NISProductDetailAdditionalInfoStyles {
    mainContainer?: InjectableCss;
    infoStyles?: InjectableCss;
    linkStyles?: InjectableCss;
    noTruncatedStyles?: InjectableCss;
    truncatedStyles?: InjectableCss;
}

export const nisProductDetailAdditionalInfoStyles: NISProductDetailAdditionalInfoStyles = {
    mainContainer: {
        css: css`
            margin-top: 24px;
            margin-bottom: 24px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    null,
                    css`
                        padding-right: 104px;
                    `,
                    css`
                        padding-right: 104px;
                    `,
                ])}
        `,
    },
    linkStyles: {
        css: css`
            cursor: pointer;
            span {
                font-family: Roboto;
                font-size: 16px;
                font-weight: 600;
                line-height: 24px;
                text-align: left;
                margin-top: 4px;
                color: #000000 !important;
            }
        `,
    },
    truncatedStyles: {
        css: css`
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 3;
            overflow: hidden;
            font-family: Roboto;
            font-size: 16px;
            font-weight: 400;
            line-height: 24px;
            color: #444444;
            ul {
                margin: 0px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            padding: 0px 0px 0px 21px;
                            font-size: 14px;
                        `,
                        css`
                            padding: 0px 0px 0px 21px;
                            font-size: 14px;
                        `,
                        null,
                        null,
                        null,
                    ])}
            }
        `,
    },
    noTruncatedStyles: {
        css: css`
            display: block;
            overflow: visible;
            font-family: Roboto;
            font-size: 16px;
            font-weight: 400;
            line-height: 24px;
            color: #444444;
            ul {
                margin: 0px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            padding: 0px 0px 0px 21px;
                            font-size: 14px;
                        `,
                        css`
                            padding: 0px 0px 0px 21px;
                            font-size: 14px;
                        `,
                        null,
                        null,
                        null,
                    ])}
            }
        `,
    },
};

const styles = nisProductDetailAdditionalInfoStyles;

export const NISProductDetailsAdditionalInfo: FC<Props> = ({ productContext }) => {
    const [truncated, setTruncate] = useState(true);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const textRef = useRef<HTMLDivElement>(null);

    const toggleStatus = (e: React.MouseEvent) => {
        e.preventDefault();
        setTruncate(!truncated);
    };

    useEffect(() => {
        if (textRef.current) {
            const element = textRef.current;
            setIsOverflowing(element.scrollHeight > element.clientHeight + 1);
        }
    }, [productContext]);

    if (!productContext?.product?.properties?.additionalInfo) {
        return null;
    }

    return (
        <StyledWrapper {...styles.mainContainer}>
            {!getIsWebCrawler() && truncated ? (
                <StyledWrapper ref={textRef} {...styles.truncatedStyles}>
                    {parse(productContext?.product?.properties?.additionalInfo, parserOptions)}
                </StyledWrapper>
            ) : (
                <StyledWrapper ref={textRef} {...styles.noTruncatedStyles}>
                    {parse(productContext?.product?.properties?.additionalInfo, parserOptions)}
                </StyledWrapper>
            )}

            {productContext?.product?.properties?.additionalInfo.length > 350 && !getIsWebCrawler() && (
                <Link href="" onClick={toggleStatus} {...styles.linkStyles}>
                    {truncated ? "See more" : "See less"}
                </Link>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, null)(withProductContext(NISProductDetailsAdditionalInfo)),
    definition: {
        group: "Basic",
        displayName: "NIS PDP AdditionalInfo",
    },
};

export default widgetModule;
